import React, { useState, useEffect } from 'react';
import { Box, Grid, Paper, Typography, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PersonIcon from '@mui/icons-material/Person';
import BarChartIcon from '@mui/icons-material/BarChart';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CubeIcon from '@mui/icons-material/AllInbox';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import StopIcon from '@mui/icons-material/Stop';
import { fetchQuestionSummary } from '../services/api';
import { lifePlanBoxKeyMap } from '../constants/lifePlanBoxKeyMap';

// Styled Paper for uniform box appearance
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5), // Compact padding
  paddingTop: theme.spacing(1), // Reduced top padding
  borderRadius: 12,
  minHeight: 160, // Increased minimum height
  background: 'rgba(255,255,255,0.12)', // Slightly lighter background
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5), // Slightly increased gap
  boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
  overflow: 'hidden', // Prevent content from overflowing
  border: '1px solid rgba(255,255,255,0.05)', // Subtle border
}));

const gridItems = [
  { title: 'Vision', icon: <MenuBookIcon fontSize="small" /> },
  { title: 'Mission', icon: <EditNoteIcon fontSize="small" /> },
  { title: 'Core Values', icon: <CubeIcon fontSize="small" /> },
  { title: 'Core Motivations', icon: <EditNoteIcon fontSize="small" /> },
  { title: 'Beneficiaries (People impacted by your life)', icon: <PersonIcon fontSize="small" /> },
  { title: 'Top Priorities (Direction)', icon: <BarChartIcon fontSize="small" /> },
  { title: 'Best Self + Strengths', icon: <EditNoteIcon fontSize="small" /> },
  { title: 'Stop', icon: <StopIcon fontSize="small" /> },
  { title: 'Start', icon: <PlayArrowIcon fontSize="small" /> },
];

// Component to fetch and display summary for a specific box
interface LifePlanBoxProps {
  title: string;
  icon: React.ReactNode;
  tall?: boolean;
  wide?: boolean;
  questionId?: number;
}

/**
 * LifePlanBox component that fetches and displays a summary for a specific box
 * in the Life Plan Canvas. It uses the callback summary API to generate summaries.
 */
const LifePlanBox: React.FC<LifePlanBoxProps> = ({ 
  title, 
  icon, 
  tall = false, 
  wide = false,
  questionId = 1 // Default to 1, but should be provided by parent
}) => {
  const [summary, setSummary] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSummary = async () => {
      if (!title || !lifePlanBoxKeyMap[title]) {
        setError('No key mapping found for this box');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Get the backend key(s) for this box title
        const backendKeys = lifePlanBoxKeyMap[title];
        
        // Create the template for the summary based on whether we have a single key or multiple keys
        let template = '';
        
        if (Array.isArray(backendKeys)) {
          // For multiple keys, create a template that includes all keys with appropriate formatting
          if (title === 'Best Self + Strengths') {
            template = `Template: My best self is: {${backendKeys[0]}}

My emotions are: {${backendKeys[1]}}

What I'm best at: {${backendKeys[2]}}

People I connect with: {${backendKeys[3]}}

My strengths are: {${backendKeys[4]}}`;
          } else if (title === 'Stop') {
            template = `Template: I will stop: {${backendKeys[0]}}, {${backendKeys[1]}}, {${backendKeys[2]}} {${backendKeys[3]}} Also stop buying into the though that {${backendKeys[4]}}`;
          } else if (title === 'Start') {
            template = `Template: I will start: {${backendKeys[0]}}, {${backendKeys[1]}}, {${backendKeys[2]}}`;
          } else {
            // Default format for multiple keys
            template = backendKeys.map(key => `{${key}}`).join(', ');
          }
        } else {
          // For a single key, just wrap it in curly braces
          template = `{${backendKeys}}`;
        }
        
        // Define custom prompts
        const customUserPrompt = `Generate a summary of the selected response that feels tight, motivating, and useful to the user. It should be short, tactical, no fluff, punchy, list-based, bold without being cheesy… Think Ben Meer-style phrasing.

Here are the responses or template to follow:
${template}`;
        const customSystemPrompt = `You are an assistant providing rephrased summaries of user's previous responses based on the style requested or template provided.`;
        
        console.log(`Fetching summary for ${title} using keys:`, backendKeys);
        console.log(`Using template: ${template}`);
        console.log(`Using custom prompts - User: ${customUserPrompt}`);
        console.log(`Using custom prompts - System: ${customSystemPrompt}`);
        
        const { summary } = await fetchQuestionSummary(
          questionId, 
          template, 
          customUserPrompt, 
          customSystemPrompt
        );
        
        setSummary(summary || 'No summary available.');
      } catch (err) {
        console.error(`Error fetching summary for ${title}:`, err);
        setError('Failed to load summary');
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [title, questionId]);

  return (
    <StyledPaper 
      sx={{ 
        ...(tall && { height: { md: 340 }, minHeight: 280 }), // Taller boxes for two-row spans
        ...(!tall && !wide && { height: 160 }), // Increased fixed height for standard boxes
        ...(wide && { width: '100%', height: 160 }), // Set height for wide box too
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'flex-start'
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5} mt={0}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ fontSize: '0.85rem' }}>{title}</Typography>
        <Box sx={{ opacity: 0.8 }}>
          {icon}
        </Box>
      </Box>
      
      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" flexGrow={1}>
          <CircularProgress size={16} />
        </Box>
      ) : error ? (
        <Typography color="error" variant="body2" sx={{ mt: 1, fontSize: '0.7rem' }}>
          {error}
        </Typography>
      ) : (
        <Box
          sx={{
            fontSize: '0.65rem', // Small text size
            lineHeight: 1.3, // Tighter line height
            overflow: 'auto',
            flexGrow: 1,
            p: 0.5,
            mt: 0, // No top margin
            maxHeight: tall ? '260px' : '130px', // Increased height limits for boxes
            opacity: 0.85, // Slightly faded text for better contrast
            color: 'text.secondary',
            whiteSpace: 'pre-line' // Preserve line breaks
          }}
        >
          {/* Format the summary text to properly display bullet points */}
          {summary.split('- ').map((item, index) => {
            // Skip the first empty item if summary starts with a bullet point
            if (index === 0 && !item.trim()) return null;
            // First item doesn't need bullet formatting if it doesn't start with a bullet
            if (index === 0 && summary.trim()[0] !== '-') {
              return <Typography key={index} variant="body2" component="div">{item}</Typography>;
            }
            // Format as bullet point
            return (
              <Typography key={index} variant="body2" component="div" sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                <Box component="span" sx={{ mr: 0.5, display: 'inline-block' }}>•</Box>
                <Box component="span">{item}</Box>
              </Typography>
            );
          })}
        </Box>
      )}
    </StyledPaper>
  );
};

// Default question ID for life plan summaries
const DEFAULT_CALLBACK_QUESTION_ID = 13945;

interface CourseCompletionGridProps {
  // The question ID to use for generating summaries
  // This should be a callback_summary type question
  callbackQuestionId?: number;
}

/**
 * CourseCompletionGrid displays a grid of Life Plan Canvas boxes with summaries.
 * Each box represents a different aspect of the user's life plan and displays
 * an AI-generated summary of their responses.
 */
const CourseCompletionGrid: React.FC<CourseCompletionGridProps> = ({ callbackQuestionId = DEFAULT_CALLBACK_QUESTION_ID }) => {
  return (
    <Box sx={{ width: '100%', p: 0, maxWidth: 1300, mx: 'auto' }}>
      {/* Use CSS Grid for more precise layout control */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(6, 1fr)',
          gridTemplateRows: 'auto auto',
          gap: 1.5,
          '& > .tall-box': {
            gridRow: 'span 2', // Make tall boxes span 2 rows
          },
          '& > .wide-box': {
            gridColumn: 'span 2', // Make wide box span 2 columns
          }
        }}
      >
        {/* First row, first column: Vision */}
        <Box>
          <LifePlanBox title={gridItems[0].title} icon={gridItems[0].icon} questionId={callbackQuestionId} />
        </Box>
        
        {/* First row, second column: Mission */}
        <Box>
          <LifePlanBox title={gridItems[1].title} icon={gridItems[1].icon} questionId={callbackQuestionId} />
        </Box>
        
        {/* Spans both rows, third column: Core Values */}
        <Box className="tall-box">
          <LifePlanBox title={gridItems[2].title} icon={gridItems[2].icon} tall questionId={callbackQuestionId} />
        </Box>
        
        {/* First row, fourth column: Core Motivations */}
        <Box>
          <LifePlanBox title={gridItems[3].title} icon={gridItems[3].icon} questionId={callbackQuestionId} />
        </Box>
        
        {/* First row, fifth column: Beneficiaries */}
        <Box>
          <LifePlanBox title={gridItems[4].title} icon={gridItems[4].icon} questionId={callbackQuestionId} />
        </Box>
        
        {/* Spans both rows, sixth column: Top Priorities */}
        <Box className="tall-box">
          <LifePlanBox title={gridItems[5].title} icon={gridItems[5].icon} tall questionId={callbackQuestionId} />
        </Box>
        
        {/* Second row, spans first and second columns: Best Self + Strengths */}
        <Box className="wide-box">
          <LifePlanBox title={gridItems[6].title} icon={gridItems[6].icon} wide questionId={callbackQuestionId} />
        </Box>
        
        {/* Second row, fourth column: Stop */}
        <Box>
          <LifePlanBox title={gridItems[7].title} icon={gridItems[7].icon} questionId={callbackQuestionId} />
        </Box>
        
        {/* Second row, fifth column: Start */}
        <Box>
          <LifePlanBox title={gridItems[8].title} icon={gridItems[8].icon} questionId={callbackQuestionId} />
        </Box>
      </Box>
    </Box>
  );
};

export default CourseCompletionGrid;
